import { useAuth } from '@clerk/nextjs';
import { datadogLogs } from '@datadog/browser-logs';
import { useLocalStorage } from '@mantine/hooks';
import type { BusinessCustomization, EndSystem, PayrollFrequency, Role } from '@prisma/client';
import { BusinessCategory, InventoryType, JobCategories } from '@prisma/client';
import type { PermissionKey } from '@schema/permission.schema';
import { glassTypes } from '@schema/supplierInventory.schema';
import type { JobSummary, JobSummaryCategoryMeta } from '@services/context.service';
import { DateHelper } from '@utils/date.helper';
import type { FeatureFlag } from '@utils/featureFlags';
import { getJobBusinessCategoryIcon } from '@utils/iconHelpers';
import type { PaymentProcessor } from '@utils/paymentProcessor';
import { titlifyEnum } from '@utils/stringHelpers';
import { DateTime } from 'luxon';
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { ErrorScreen } from '~/components/error-handling/ErrorScreen';
import { LoadingScreen } from '~/components/LoadingScreen';
import type { ContextRouterOutput } from '~/server/routers/types';
import { trpc } from '~/utils/trpc';

export type CategoryOption = {
	collectsVehicleInfo: boolean;
	color: string | null;
	defaultsToProgressBilling: boolean;
	icon: string | null;
	id: number | null;
	isDefault: boolean;
	legacyCategory: BusinessCategory | null;
	name: string;
	useBusinessAddress: boolean;
};

export interface IUserState {
	allowedPermissions: PermissionKey[];
	autoSyncQuickbooks: boolean;
	useUnappliedPaymentsByDefault: boolean;
	businessCategorySummary: JobSummary | undefined;
	// Label, icon, color and order for every category the summary can key on — hidden rows
	// included, since a job can still sit in one.
	businessCategoryMeta: JobSummaryCategoryMeta[] | undefined;
	businessId: number;
	businessName: string | null;
	businessTimezone: string;
	defaultGlassCostCodeId: number | null;
	defaultLaborCostCodeId: number | null;
	defaultMaterialCostCodeId: number | null;
	defaultQuickEstimateBusinessCategory: BusinessCategory;
	defaultQuickEstimateJobCategoryId: number | null;
	email: string | null;
	emailDomain: string | null;
	emailSignature: string | null;
	featureFlags: FeatureFlag[];
	floorPlanScale: number;
	firstName: string | null;
	estimateColumnState: string | null;
	purchaseOrderColumnState: string | null;
	hasMultipleBusinesses: boolean;
	id: number | null;
	impersonatingUser: string | null;
	isBiweeklyOffset: boolean;
	isEmployeeApprovalRequiredForTimecardEntries: boolean;
	isOfficeApprovalRequiredForTimecardEntries: boolean;
	isQuoteLineItemToggleEnabled: boolean;
	isShownOnSchedule: boolean;
	isSuperAdmin: boolean;
	isUsingManualTimeTracking: boolean;
	isUsingSingleActualCostForPurchaseOrders: boolean;
	autoMarkPoAsAcceptedOnFinalDelivery: boolean;
	lastName: string | null;
	useInvoiceLineItems: boolean;
	payPeriodStartDay: number;
	payrollFrequency: PayrollFrequency;
	phone: string | null;
	quickbooksEnabled: boolean | null;
	quickbooksEndSystem: EndSystem | null;
	jobsUseQBClass: boolean;
	quickEstimatesCount: number | undefined;
	role: Role;
	shouldBccOnSentEmails: boolean;
	shouldSendEmailNotifications: boolean;
	shouldSendSmsNotifications: boolean;
	shouldReceiveWebNotifications: boolean;
	shouldReceiveMentionNotifications: boolean;
	shouldReceiveNoteNotifications: boolean;
	shouldReceivePaymentNotifications: boolean;
	shouldReceiveTextMessageNotifications: boolean;
	shouldReceiveKeyDateNotifications: boolean;
	shouldReceiveTaskNotifications: boolean;
	shouldReceiveJobTaskNotifications: boolean;
	shouldReceiveDailyReportNotifications: boolean;
	shouldReceiveSignatureNotifications: boolean;
	shouldReceiveWorkOrderNotifications: boolean;
	hasUserOptedOutOfSmsBySmsMessage: boolean;
	streamChatUserToken: string | null;
	enableIntercomHints: boolean;
	embedUuid: string | null;
	openLeadsCount: number | undefined;
	useScheduleCustomColor: boolean;
	scheduleJobsAreConfirmed: boolean;
	showWorkOrdersInSchedulingPool: boolean;
	enableInternalJobId: boolean;
	isCustomerChatEnabled: boolean;

	// Paid / Gated Features.
	enableCompanyChat: boolean;
	enableDailyReports: boolean;
	enableFloorplans: boolean;
	enableInventory: boolean;
	enableTimeTracking: boolean;
	enableSmartSchedule: boolean;
	enableSmartTakeoff: boolean;
	enableGlazierChat: boolean;
	enable2WayEmail: boolean;
	enablePlanAnalysis: boolean;
	customizations: BusinessCustomization[];
	enableProposalBuilder: boolean;
	enableRevenueAgent: boolean;
	enableReachMarketing: boolean;
	enableReachMarketingDemo: boolean;
	enableReachAiVoice: boolean;
	enableReachAiVoiceDemo: boolean;
	enableAlivo: boolean;
	/**
	 * `enableLeads || enableReachMarketing || enableReachAiVoice`. Either Reach flag admits on its own:
	 * Leads is where a Reach business reads the leads Reach produced, so the two are sold as one thing.
	 * Every Leads surface reads this rather than re-spelling the OR. Mirrored server-side by
	 * `leadsProcedure` in lead.router.ts.
	 */
	isLeadsEnabled: boolean;
	/** `Business.enableJobPlaybooks`, renamed on the way in — see the mapping below for why. */
	isPlaybooksFeatureEnabled: boolean;

	tasksAssignedByMeCount?: number;
	tasksAssignedToMeCount?: number;
	taskCountWithEmailDeliveryFailed?: number;
	paymentProcessor: PaymentProcessor | null;
	/** Whether this business can take payments at all — i.e. some processor is configured. Prefer this
	 *  over comparing `paymentProcessor` yourself; use `paymentProcessor` only to branch Tilled vs Payabli. */
	checkoutEnabled: boolean;
	payabliEntrypoint: string | null;
	tilledAccountId: string | null;
	unreadNotificationsCount?: number;
	visibility: {
		hiddenArrays: {
			hiddenBusinessCategories: BusinessCategory[];
			hiddenJobCategories: JobCategories[];
			hiddenInventoryTypes: InventoryType[];
		};
		getDefaultOptions: {
			getDefaultBusinessCategory: (defaultOption?: BusinessCategory) => BusinessCategory;
			getDefaultJobCategory: (defaultOption?: JobCategories) => JobCategories;
			getDefaultInventoryType: (defaultOption?: InventoryType) => InventoryType;
		};
		// The business's own job categories, ordered and excluding hidden ones. Replaces
		// deriving the list from the BusinessCategory enum, which cannot represent a
		// category a business defined itself.
		jobCategories: ContextRouterOutput['forUser']['jobCategories'];
		// What every category picker should render, custom categories included. `id` is
		// null only on the no-rows degradation, where options are synthesized from the
		// enum and submits fall back to the legacy value.
		categoryOptions: CategoryOption[];
		// Label and icon for a category, taken from the business's own row so a renamed or
		// custom category shows what the business called it. Accepts a row id, a legacy enum
		// or a name; pass the id wherever a record carries one, since the enum column beside
		// it is an inert placeholder on a custom category. Falls back to titlifying the enum
		// for values the business has no row for.
		getJobCategoryLabel: (category: BusinessCategory | number | string | null | undefined) => string;
		// The business's row id for the same inputs, or undefined when it has no row for them.
		getJobCategoryId: (category: BusinessCategory | number | string | null | undefined) => number | undefined;
		getJobCategoryIcon: (category: BusinessCategory | number | string | null | undefined) => { icon: string; color: string };
		filteredOptions: {
			filteredBusinessCategories: BusinessCategory[];
			filteredJobCategories: JobCategories[];
			filteredInventoryTypes: InventoryType[];
			filteredGlassTypes: InventoryType[];
		};
	};
}

const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000;

export const UserContext = createContext<IUserState | null>(null);

export const useUserContext = () => {
	const userContext = useContext(UserContext);

	if (!userContext) {
		throw new Error('User Context Data Error');
	}

	return userContext;
};

export const useGpsTimeTrackingEnabled = () => {
	const { enableTimeTracking, isUsingManualTimeTracking } = useUserContext();

	return enableTimeTracking && !isUsingManualTimeTracking;
};

type Props = {
	children: ReactNode;
};

export const UserProvider = (props: Props) => {
	const { actor, isLoaded, userId } = useAuth();
	// localStorage, not sessionStorage: a session-scoped flag re-nags anyone who permanently works
	// outside the business timezone in every new tab, every day.
	// `UserProvider` itself remounts on every navigation between pages on different layouts
	// (DefaultLayout/JobLayout/FieldLayout/PDF layouts), so `getInitialValueInEffect` (Mantine's
	// default) would read back `defaultValue` on the first effect pass of every such remount,
	// defeating the 30-day suppression below on every layout switch.
	const [lastTimezoneWarning, setLastTimezoneWarning] = useLocalStorage<{ timezone: string; shownAt: number } | null>({
		key: 'timezoneMismatchWarning',
		defaultValue: null,
		getInitialValueInEffect: false,
	});
	const userContextQuery = trpc.context.forUser.useQuery();
	const { data: webNavigationData } = trpc.context.forWebNavigation.useQuery();

	const { data } = userContextQuery;
	// Read the scalar off `actor` rather than depending on the object itself: Clerk mints a new
	// `actor` on every JWT refresh (~every 50s), so an `[actor]` dependency would rebuild this
	// value — and re-render every consumer in the app — even though nothing actually changed.
	const impersonatingUser = actor?.sub ?? null;

	// Must run on every render, before any conditional return: this used to be built inside the
	// `isSuccess` branch, which makes it a conditional hook and throws "Rendered more hooks than
	// during the previous render" the moment the query settles.
	const userContextValues = useMemo<IUserState | null>(() => {
		if (!data) {
			return null;
		}

		const { hiddenBusinessCategories, hiddenJobCategories, hiddenInventoryTypes } = data;

		// Derived from the business's own categories rather than the enum, so hiding is
		// driven by deletedAt on the row. Built-ins still map back to an enum value while
		// the write path expects one; a custom category has no enum value and is skipped
		// here until the wire format moves to ids.
		// Keyed on legacyCategory rather than the name: the name is what a business renames,
		// and keying on it made a renamed built-in vanish from every selection control.
		const jobCategoryByLegacy = new Map(
			data.jobCategories.filter((jobCategory) => jobCategory.legacyCategory).map((jobCategory) => [jobCategory.legacyCategory, jobCategory])
		);
		const jobCategoryByName = new Map(data.jobCategories.map((jobCategory) => [jobCategory.name, jobCategory]));
		// Keyed by String(id): summary buckets and category URLs identify a category by its
		// row id now. The navigation meta covers hidden rows too — a job can still sit in
		// one and its tab needs a label.
		const jobCategoryByIdKey = new Map<string, { id: number; name: string; icon: string | null; color: string | null }>([
			...(webNavigationData?.categoryMeta ?? []).map((meta) => [String(meta.id), meta] as const),
			...data.jobCategories.map((jobCategory) => [String(jobCategory.id), jobCategory] as const),
		]);
		const displayFor = (category: BusinessCategory | number | string | null | undefined) => {
			if (!category) return undefined;
			// Callers pass a category row id (as a number or number-string), a legacy enum
			// value, or an already-resolved category name.
			const key = String(category);

			return jobCategoryByIdKey.get(key) ?? jobCategoryByLegacy.get(key) ?? jobCategoryByName.get(key);
		};

		const isBusinessCategoryValue = (value: string): value is BusinessCategory => value in BusinessCategory;
		const categoryOptions: CategoryOption[] = data.jobCategories.length
			? data.jobCategories.map((jobCategory) => ({
					collectsVehicleInfo: jobCategory.collectsVehicleInfo,
					color: jobCategory.color,
					defaultsToProgressBilling: jobCategory.defaultsToProgressBilling,
					icon: jobCategory.icon,
					id: jobCategory.id,
					isDefault: jobCategory.isDefault,
					legacyCategory:
						jobCategory.legacyCategory && isBusinessCategoryValue(jobCategory.legacyCategory) ? jobCategory.legacyCategory : null,
					name: jobCategory.name,
					useBusinessAddress: jobCategory.useBusinessAddress,
				}))
			: Object.values(BusinessCategory)
					.filter((category) => !data.hiddenBusinessCategories.includes(category))
					.map((category) => ({
						collectsVehicleInfo: category === BusinessCategory.AUTO_GLASS,
						color: null,
						defaultsToProgressBilling: category === BusinessCategory.COMMERCIAL,
						icon: null,
						id: null,
						isDefault: category === BusinessCategory.RESIDENTIAL,
						legacyCategory: category,
						name: titlifyEnum(category),
						useBusinessAddress: category === BusinessCategory.AUTO_GLASS || category === BusinessCategory.PICKUP,
					}));

		// A business whose rows resolve to nothing would otherwise blank every category
		// picker in the app. That is not hypothetical — businesses created between the
		// phase 1 backfill and provisioning have no rows, and rows written before
		// provisioning carried legacyCategory resolve to nothing here. The fallback keys
		// on what actually resolved, not on the row count, so both cases degrade to the
		// old enum behavior instead of rendering an empty picker.
		const categoriesFromRows = data.jobCategories
			.map((jobCategory) => jobCategory.legacyCategory)
			.filter((value): value is BusinessCategory => value !== null && value in BusinessCategory);

		// These stay inside the memo because the `getDefaultOptions` callbacks below close over
		// them; hoisting them out would give those callbacks a new identity on every render.
		const filteredBusinessCategories = categoriesFromRows.length
			? categoriesFromRows
			: Object.values(BusinessCategory).filter((category) => !hiddenBusinessCategories.includes(category));
		const filteredJobCategories = Object.values(JobCategories).filter((category) => !hiddenJobCategories.includes(category));
		const filteredInventoryTypes = Object.values(InventoryType).filter((type) => !hiddenInventoryTypes.includes(type));
		const filteredGlassTypes = glassTypes.filter((type) => !hiddenInventoryTypes.includes(type));

		return {
			allowedPermissions: data.allowedPermissions,
			autoSyncQuickbooks: data.autoSyncQuickbooks,
			useUnappliedPaymentsByDefault: data.useUnappliedPaymentsByDefault,
			businessCategorySummary: webNavigationData?.businessCategorySummaries,
			businessCategoryMeta: webNavigationData?.categoryMeta,
			businessId: data.businessId,
			businessName: data.businessName,
			businessTimezone: data.businessTimezone,
			defaultGlassCostCodeId: data.defaultGlassCostCodeId,
			defaultLaborCostCodeId: data.defaultLaborCostCodeId,
			defaultMaterialCostCodeId: data.defaultMaterialCostCodeId,
			defaultQuickEstimateBusinessCategory: data.defaultQuickEstimateBusinessCategory,
			defaultQuickEstimateJobCategoryId: data.defaultQuickEstimateJobCategoryId,
			email: data.email,
			emailDomain: data.emailDomain,
			emailSignature: data.emailSignature,
			featureFlags: data.featureFlags,
			floorPlanScale: data.floorPlanScale,
			firstName: data.firstName,
			estimateColumnState: data.estimateColumnState,
			purchaseOrderColumnState: data.purchaseOrderColumnState,
			hasMultipleBusinesses: data.hasMultipleBusinesses,
			id: data.id,
			impersonatingUser,
			isBiweeklyOffset: data.isBiweeklyOffset,
			isEmployeeApprovalRequiredForTimecardEntries: data.isEmployeeApprovalRequiredForTimecardEntries,
			isOfficeApprovalRequiredForTimecardEntries: data.isOfficeApprovalRequiredForTimecardEntries,
			isQuoteLineItemToggleEnabled: data.isQuoteLineItemToggleEnabled,
			isShownOnSchedule: data.isShownOnSchedule,
			isSuperAdmin: data.isSuperAdmin,
			isUsingManualTimeTracking: data.isUsingManualTimeTracking,
			isUsingSingleActualCostForPurchaseOrders: data.isUsingSingleActualCostForPurchaseOrders,
			autoMarkPoAsAcceptedOnFinalDelivery: data.autoMarkPoAsAcceptedOnFinalDelivery,
			lastName: data.lastName,
			useInvoiceLineItems: data.useInvoiceLineItems,
			payPeriodStartDay: data.payPeriodStartDay,
			payrollFrequency: data.payrollFrequency,
			phone: data.phone,
			quickbooksEnabled: data.hasQuickbooks,
			quickbooksEndSystem: data.quickbooksEndSystem,
			quickEstimatesCount: webNavigationData?.quickEstimatesCount,
			role: data.role as Role,
			shouldBccOnSentEmails: data.shouldBccOnSentEmails,
			shouldSendEmailNotifications: data.shouldSendEmailNotifications,
			shouldReceiveDailyReportNotifications: data.shouldReceiveDailyReportNotifications,
			shouldReceiveKeyDateNotifications: data.shouldReceiveKeyDateNotifications,
			shouldReceiveTaskNotifications: data.shouldReceiveTaskNotifications,
			shouldReceiveJobTaskNotifications: data.shouldReceiveJobTaskNotifications,
			shouldSendSmsNotifications: data.shouldSendSmsNotifications,
			shouldReceiveWebNotifications: data.shouldReceiveWebNotifications,
			shouldReceiveMentionNotifications: data.shouldReceiveMentionNotifications,
			shouldReceiveNoteNotifications: data.shouldReceiveNoteNotifications,
			shouldReceivePaymentNotifications: data.shouldReceivePaymentNotifications,
			shouldReceiveTextMessageNotifications: data.shouldReceiveTextMessageNotifications,
			shouldReceiveSignatureNotifications: data.shouldReceiveSignatureNotifications,
			shouldReceiveWorkOrderNotifications: data.shouldReceiveWorkOrderNotifications,
			hasUserOptedOutOfSmsBySmsMessage: data.hasUserOptedOutOfSmsBySmsMessage,
			streamChatUserToken: data.streamChatUserToken,
			enableIntercomHints: data.enableIntercomHints,
			embedUuid: data.embedUuid,
			openLeadsCount: webNavigationData?.openLeadsCount,

			useScheduleCustomColor: data.useScheduleCustomColor,
			scheduleJobsAreConfirmed: data.scheduleJobsAreConfirmed,
			showWorkOrdersInSchedulingPool: data.showWorkOrdersInSchedulingPool,
			enableInternalJobId: data.enableInternalJobId,
			isCustomerChatEnabled: data.isCustomerChatEnabled,
			jobsUseQBClass: data.jobsUseQBClass,

			// Paid / Gated Features
			enableCompanyChat: data.enableCompanyChat,
			enableDailyReports: data.enableDailyReports,
			enableFloorplans: data.enableFloorplans,
			enableInventory: data.enableInventory,
			enableTimeTracking: data.enableTimeTracking,
			enableSmartSchedule: data.enableSmartSchedule,
			enableSmartTakeoff: data.enableSmartTakeoff,
			enableGlazierChat: data.enableGlazierChat,
			enable2WayEmail: data.enable2WayEmail,
			enablePlanAnalysis: data.enablePlanAnalysis,
			customizations: data.customizations,
			enableProposalBuilder: data.enableProposalBuilder,
			enableRevenueAgent: data.enableRevenueAgent,
			enableReachMarketing: data.enableReachMarketing,
			enableReachMarketingDemo: data.enableReachMarketingDemo,
			enableReachAiVoice: data.enableReachAiVoice,
			enableReachAiVoiceDemo: data.enableReachAiVoiceDemo,
			enableAlivo: data.enableAlivo,
			// Derived rather than passed through, so no surface has to remember that Reach implies Leads.
			isLeadsEnabled: data.enableLeads || data.enableReachMarketing || data.enableReachAiVoice,
			// Renamed: `JobPlaybook.isEnabled` is a different, per-category switch, and the two sit together
			// in the settings editor.
			isPlaybooksFeatureEnabled: data.enableJobPlaybooks,

			tasksAssignedByMeCount: webNavigationData?.tasksAssignedByMeCount,
			tasksAssignedToMeCount: webNavigationData?.tasksAssignedToMeCount,
			taskCountWithEmailDeliveryFailed: webNavigationData?.taskCountWithEmailDeliveryFailed,
			paymentProcessor: data.paymentProcessor,
			checkoutEnabled: data.paymentProcessor != null,
			payabliEntrypoint: data.payabliEntrypoint,
			tilledAccountId: data.tilledAccountId,
			unreadNotificationsCount: webNavigationData?.unreadNotificationsCount,
			visibility: {
				hiddenArrays: {
					hiddenBusinessCategories,
					hiddenJobCategories,
					hiddenInventoryTypes,
				},
				getDefaultOptions: {
					getDefaultBusinessCategory: (defaultOption = BusinessCategory.RESIDENTIAL) =>
						filteredBusinessCategories.includes(defaultOption) ? defaultOption : (filteredBusinessCategories[0] ?? defaultOption),
					getDefaultJobCategory: (defaultOption = JobCategories.WINDOW) =>
						filteredJobCategories.includes(defaultOption) ? defaultOption : (filteredJobCategories[0] ?? defaultOption),
					getDefaultInventoryType: (defaultOption = InventoryType.SINGLE_LITE_GLASS) =>
						filteredInventoryTypes.includes(defaultOption) ? defaultOption : (filteredInventoryTypes[0] ?? defaultOption),
				},
				jobCategories: data.jobCategories,
				categoryOptions,
				getJobCategoryLabel: (category) => displayFor(category)?.name ?? (typeof category === 'string' ? titlifyEnum(category) : ''),
				getJobCategoryId: (category) => displayFor(category)?.id,
				getJobCategoryIcon: (category) => {
					const jobCategory = displayFor(category);
					const fallback = getJobBusinessCategoryIcon(typeof category === 'number' ? undefined : (category as BusinessCategory));
					return { icon: jobCategory?.icon ?? fallback.icon, color: jobCategory?.color ?? fallback.rawColor };
				},
				filteredOptions: {
					filteredBusinessCategories,
					filteredJobCategories,
					filteredInventoryTypes,
					filteredGlassTypes,
				},
			},
		};
	}, [data, impersonatingUser, webNavigationData]);

	// Warning the user about a device/business timezone mismatch is a side effect — it toasts, logs,
	// and writes to local storage — so it belongs in an effect rather than the render body, where
	// it used to set the seen flag mid-render. Depends on `data`, not on the memo above, so it does
	// not re-run when unrelated context fields change.
	const businessTimezone = data?.businessTimezone;

	useEffect(() => {
		if (!businessTimezone) {
			return;
		}

		const localTimezone = DateTime.local().zoneName;
		if (DateHelper.timezonesShareRules({ otherTimezone: businessTimezone, timezone: localTimezone })) {
			return;
		}

		// Re-warn on a new device timezone even inside the month: someone who flies Denver -> Tokyo
		// has a different problem than the one they were already told about.
		const isStale =
			!lastTimezoneWarning || lastTimezoneWarning.timezone !== localTimezone || Date.now() - lastTimezoneWarning.shownAt > ONE_MONTH_MS;
		if (!isStale) {
			return;
		}

		toast.warn(
			<>
				<p>Your device's timezone is {localTimezone}.</p>
				<p>Dates and times will be displayed in your business timezone ({businessTimezone}).</p>
			</>,
			{
				autoClose: false,
				closeOnClick: false,
				draggable: false,
				hideProgressBar: true,
				position: 'top-center',
				toastId: 'timezone warning',
			}
		);

		datadogLogs.logger.warn('Device/business timezone mismatch', {
			localTimezone,
			businessTimezone,
		});

		setLastTimezoneWarning({ timezone: localTimezone, shownAt: Date.now() });
	}, [businessTimezone, lastTimezoneWarning, setLastTimezoneWarning]);

	if (isLoaded && userId) {
		// Check isError/isLoading before falling back to userContextValues: react-query retains the
		// previous successful `data` when a refetch fails, so `userContextValues` stays truthy on a
		// failed refetch. Checking it first would silently keep rendering stale permissions, feature
		// flags, and business settings instead of surfacing the error.
		if (userContextQuery.isError) {
			return (
				<ErrorScreen
					status={userContextQuery.error?.data?.code === 'NOT_IMPLEMENTED' ? 'METHOD_NOT_SUPPORTED' : userContextQuery.error?.data?.code}
				/>
			);
		} else if (userContextValues) {
			return <UserContext.Provider value={userContextValues}>{props.children}</UserContext.Provider>;
		} else if (userContextQuery.isLoading) {
			return <LoadingScreen srText="Loading" />;
		} else {
			return <ErrorScreen message={'Error loading user.'} />;
		}
	} else if (userId === null) {
		return <ErrorScreen message={'No auth ID found.'} />;
	} else {
		return <LoadingScreen srText="Loading" />;
	}
};
